import React, { useEffect, useState } from 'react'
import { Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, IconButton, InputLabel, List, ListItem, ListItemButton, ListItemIcon, ListItemSecondaryAction, ListItemText, MenuItem, Paper, Select, Step, StepContent, StepLabel, Stepper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, Link, styled, ListSubheader, Alert } from '@mui/material';
import axios from 'axios';
import { Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import WarningIcon from '@mui/icons-material/Warning';
import { Helmet } from 'react-helmet';

export default function Inventory() {
  return (
    <Routes>
      <Route path="privacy" element={<Privacy />} />
      <Route path="/" element={<InventoryApp />} />
    </Routes>
  )
}

const Paragraph = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

function Privacy() {
  return (
    <>
      <Helmet>
        {/* https://developers.google.com/search/docs/crawling-indexing/block-indexing */}
        <meta name="robots" content="noindex" />
      </Helmet>
      <Box padding={2} sx={{width: '100%'}}>
        <Typography variant="h6">Privacy Policy</Typography>
        <Paragraph>
          This web app presents a friendly interface for using the <Link href="https://docs.google.com/spreadsheets/d/1WqIVkpmwcyVD8QuEqVsVZJA7WrKbjrmK8xwRZQbMox4/edit?gid=1150664769#gid=1150664769" color="inherit">Liau group ordering spreadsheet</Link>. To provide this service, you will be prompted to input some information. We do not collect any data other than what is directly inputted by the user. We share this data only with Google because the Liau group ordering spreadsheet is hosted on Google Sheets, and we need privileges from your Google account to access it. Once you login securely through Google, we will access your given (first) name, which we will add to your ordering requests in the Google spreadsheet. We do not use any other user data. We will not have access to your Google account password. We do not store any user data on our servers.
        </Paragraph><Paragraph>
          When you login to your Google account, you will also be asked to grant permission to view and manage your spreadsheets in Google Drive. But note that this app will <i>only</i> access the Liau group supply ordering spreadsheet. We do not access anything else in your Google Drive. We will parse the data in the Liau group ordering spreadsheet to present it in a user-friendly manner, prompt the user to indicate which supplies they would like to order, and then submit the order to the spreadsheet. We will not modify any other data in the spreadsheet. We do not store any of this spreadsheet data or user-inputted data on our servers.
        </Paragraph><Paragraph>
          Security procedures are in place to protect the confidentiality of your data. Because we do not store any of your data on our servers but rather send it directly to Google Sheets, you should also consult Google's privacy policy. If you wish to delete your data from the Liau group ordering spreadsheet, delete the corresponding rows and/or consult Google's data retention policy.
        </Paragraph>
      </Box>
    </>
  )
}

function InventoryApp() {
  const [activeStep, setActiveStep] = useState(0)
  let [searchParams, setSearchParams] = useSearchParams()
  const [token, setToken] = useState(null)
  const [selectedCategories, setSelectedCategories] = useState([])
  const [preview, setPreview] = useState(null)
  const [preview_error, setPreview_Error] = useState(null)
  const [updates, setUpdates] = useState(null)
  const [summary, setSummary] = useState(null)
  const [data, setData] = useState([])
  const location = useLocation()
  const [loginMessage, setLoginMessage] = useState(null)

  const handleLogin = () => {
    axios.get('/api/public/inventory/login').then(({data}) => {
      if (data.authenticated)
        console.log('Logged in!')
      else
        window.location.href = data.authorization_url
    })
  }

  useEffect(() => {
    if (searchParams.size > 0) {
      axios.post('/api/public/inventory/token', Object.fromEntries(searchParams.entries())).then(({data}) => {
        if (data.success) {
          setToken(data.token)
          setLoginMessage(null)
        }
        else
          setLoginMessage(data.message || 'Failed to log in')
      })
      setSearchParams()
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (token && activeStep === 0)
      setActiveStep(1)
  }, [token, activeStep])

  const handleSelectCategory = (categories) => {
    setSelectedCategories(categories)
    setActiveStep(2)
  }

  const handleProceedToReview = () => {
    axios.get('/api/public/inventory/get_preview', {
      params: {
        items: data.map(({Name}) => Name),
        quantities: data.map(({qty}) => qty),
        urgency: data.map(({urgency}) => urgency),
      },
      paramsSerializer: {indexes: null},
      headers: {
        'X-GoogleOAuth-Token': token
      }
    }).then(({data : { success, table, error }}) => {
      if (success) {
        setPreview(table)
        console.log(success, table, error)
      }
      else
        setPreview_Error(error)
    })
    setActiveStep(3)
  }

  const handleBack = () => {
    setPreview(null)
    setPreview_Error(null)
    setActiveStep(prev => prev - 1)
  }

  const handleSubmit = () => {
    axios.post('/api/public/inventory/submit', preview, {
      headers: {
        'X-GoogleOAuth-Token': token
      }
    }).then(({data : {success, updates, summary}}) => {
      if (!success) {
        alert('Failed to submit')
        return
      }
      setUpdates(updates)
      setSummary(summary)
      setActiveStep(4)
    })
  }

  const colItemQuery = preview?.columns?.findIndex(header => header === "item")
  const colItemDiff = preview?.columns?.findIndex(header => header === "Item name")
  const colWarning = preview?.columns?.findIndex(header => header === "warning")

  return (
    <Box padding={2} sx={{width: '100%'}}>
      <Stepper activeStep={activeStep} orientation="vertical">
        <Step>
          <StepLabel>Login</StepLabel>
            <StepContent>
              <Button onClick={handleLogin} variant="contained">Login with Google</Button>
              {loginMessage && <Typography color="error">{loginMessage}</Typography>}
              <Typography sx={{ mt: 1 }}>Welcome! This web app presents a friendly interface for using the Liau group supply ordering spreadsheet. Here's our <Link href={`${location.pathname}/privacy`} color='inherit'>privacy policy</Link>.</Typography>
              <Box
                component="img"
                sx={{
                  maxHeight: { xs: 350 },
                  maxWidth: { xs: '100%' },
                  display: 'block',
                  mt: 1,
                }}
                alt="Screenshot showing app functionality."
                src="/assets/frab-screenshot.jpg"
              />
              <Typography sx={{ mt: 1 }}>In the Liau lab, each member manages a set of common supplies and fills out a Google Sheets spreadsheet weekly for any items that need to be re-stocked. This applet offers a mobile-friendly interface to streamline this process — simply tap the checkbox to go through in-stock items or tap the shopping cart to add an order to the spreadsheet. Behind the scenes, the app auto-fills details like price and storage location, reducing manual entry.</Typography>
            </StepContent>
          </Step>
          <Step>
            <StepLabel>Select inventory category</StepLabel>
            <StepContent>
              <WhoAmI token={token} onSubmit={handleSelectCategory} />
            </StepContent>
          </Step>
          <Step>
            <StepLabel>Order supplies</StepLabel>
            <StepContent>
              <Typography variant='body2'>Click the cart icon for items to be ordered and then indicate the quantity and urgency. The checkboxes don't do anything — they're just for your convenience to track your progress. This list is taken from the Liau Group Orders spreadsheet, Common Biology Supplies tab, filtered by the supply categor{selectedCategories.length > 1 ? 'ies' : 'y'} {selectedCategories.join(', ')}. </Typography>
              <InventoryList
                token={token}
                selectedCategories={selectedCategories}
                data={data}
                setData={setData}
                onSubmit={handleProceedToReview}
                onBack={handleBack}
              />
            </StepContent>
          </Step>
          <Step>
            <StepLabel>Review and submit</StepLabel>
            <StepContent>
              <Typography variant='body2'>Here are the items in your cart. They've been matched to items previously ordered to auto-fill the price, date last ordered, and location. The red/green edits, if any, in the item name indicate differences between the name of the item in your cart and the name of best matching item from previous orders.</Typography>
              {preview_error && <Alert severity="error" sx={{my: 2}}>{preview_error}</Alert>}
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {preview?.columns?.map((item, i) => i === colItemQuery || i === colWarning ? null : <TableCell key={i}>{item}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview?.data?.map((row, i) => (
                      <TableRow key={i}>
                        {row.map((item, j) => {
                          if (j === colItemDiff)
                            return <TableCell key={j}>
                              <Typography sx={{display: 'inline'}}>{item.map(([op, val], i) => {
                                  if (op === '+')
                                    return <span key={i} style={{ backgroundColor: 'green' }}>{val}</span>
                                  if (op === '-')
                                    return <span key={i} style={{ textDecoration: 'line-through', backgroundColor: 'red' }}>{val}</span>
                                  return <span key={i}>{val}</span>
                                })}
                              </Typography>
                              {row?.[colWarning] && 
                                <Tooltip
                                  title={row[colWarning]}
                                  sx={{display: 'inline', ml: 1, verticalAlign: 'middle'}}
                                >
                                  <WarningIcon color="warning" fontSize="small" />
                                </Tooltip>
                              }
                            </TableCell>
                          else if (j === colItemQuery || j === colWarning)
                            return null
                          return <TableCell key={j}>{item}</TableCell>
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={preview === null}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Submit
                </Button>
                <Button
                  onClick={handleBack}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Back
                </Button>
              </Box>
            </StepContent>
          </Step>
          <Step>
            <StepLabel>Done</StepLabel>
            <StepContent>
              Successfully updated {updates?.updatedRows} row{updates?.updatedRows === 1 ? '' : 's'} with {updates?.updatedColumns} column{updates?.updatedColumns === 1 ? '' : 's'} in the range {updates?.updatedRange}.

              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => window.open(`https://docs.google.com/spreadsheets/d/${updates?.spreadsheetId}/edit?range=${updates?.updatedRange?.split("!")?.[1]}`, "_blank")}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Go to Spreadsheet
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    alert("Copied to clipboard!")
                    navigator.clipboard.writeText(summary)
                  }}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Copy Summary
                </Button>
              </Box>
            </StepContent>
          </Step>
      </Stepper>
      {/* {activeStep === steps.length && (
        <Paper square elevation={0} sx={{ p: 3 }}>
          <Typography>All steps completed - you&apos;re finished</Typography>
          <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
            Reset
          </Button>
        </Paper>
      )} */}
    </Box>
  )
}

function WhoAmI({token, onSubmit}) {
  const [data, setData] = useState(undefined)
  const [selectedCategories, setSelectedCategories] = useState([])
  useEffect(() => {
    axios.get('/api/public/inventory/whoami', {
      headers: {
        'X-GoogleOAuth-Token': token
      }
    }).then(({data}) => {
      setData(data)
      if (data.suggested_categories)
        setSelectedCategories(data.suggested_categories)
    })
  }, [token])
  const handleCheck = (e) => {
    if (e.target.checked)
      setSelectedCategories([...selectedCategories, e.target.name])
    else
      setSelectedCategories(selectedCategories.filter(category => category !== e.target.name))
  }
  const handleSubmit = () => {
    onSubmit?.(selectedCategories)
  }
  return (
    <Box>
      <Typography>Hi {data?.user}, which supplies do you order?</Typography>
      <FormGroup>
        {data?.categories.map((category, i) => (
          <FormControlLabel key={i} control={<Checkbox name={category} />} label={category} checked={selectedCategories.includes(category)} onChange={handleCheck} />
        ))}
      </FormGroup>
      <Typography variant='body2'>These categories are taken from the Liau Group Orders spreadsheet, Common Biology Supplies tab.</Typography>
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{ mt: 1, mr: 1 }}
          disabled={data === undefined}
        >
          Next
        </Button>
        {data?.hint &&
          <Tooltip title={data?.hint} sx={{ml: 1}}>
            <IconButton>
              <TipsAndUpdatesIcon />
            </IconButton>
          </Tooltip>
        }
      </Box>
    </Box>
  )
}

export function InventoryList({token, selectedCategories, onSubmit, onBack, data, setData}) {
  const [list, setList] = useState(undefined)
  useEffect(() => {
    axios.get(`/api/public/inventory/get_list`, {
      params: {categories: selectedCategories},
      paramsSerializer: {indexes: null},
      headers: {
        'X-GoogleOAuth-Token': token
      }
    }).then(({data : {items, headers}}) => {
      setList(items.map(({location, items: subitems}, k) => ({
        location,
        items: subitems.map((item, i) =>
          ({
            id: `${k}-${i}`,
            checked: false,
            ...Object.fromEntries(item.map((val, j) => [headers[j], val]))
          })
        )})
      ))
    })
  }, [token, selectedCategories])
  const handleCheck = (item) => {
    setList(prev => prev.map(({items, ...rest}) => ({...rest, items: items.map(x => x.id === item.id ? {...x, checked: !item.checked} : x)})))
  }
  const handleAddToCart = (item) => {
    setData(prev => [...prev, {...item, qty: 1, urgency: "Medium"}])
  }
  const handleChangeQty = (item, e) => {
    setData(prev => prev.map(x => x.id === item.id ? {...x, qty: e.target.value} : x))
  }
  const handleChangeUrgency = (item, e) => {
    setData(prev => prev.map(x => x.id === item.id ? {...x, urgency: e.target.value} : x))
  }
  const handleBlur = (item) => {
    const item_qty = data.find(x => x.id === item.id)?.qty
    if (item_qty === "" || item_qty === "0")
      setData(prev => prev.filter(x => x.id !== item.id))
  }
  const handleSubmit = () => {
    onSubmit?.()
  }
  const handleBack = () => {
    onBack?.()
  }
  return (
    <Box>
      <List>
        {list?.map(({location, items}, k) => 
          <React.Fragment key={k}>
            <ListSubheader>{location}</ListSubheader>
            {items.map((item, i) => {
              const item_in_cart = data.find(x => x.id === item.id)
              return (
                <ListItem disablePadding dense onClick={() => handleCheck(item)} sx={{pr: item_in_cart ? "170px" : null}} key={item.id} >
                  <ListItemButton>
                    <ListItemIcon sx={{minWidth: 0, pr: 1}}>
                      <Checkbox
                        edge="start"
                        tabIndex={-1}
                        disableRipple
                        onChange={() => handleCheck(item)}
                        checked={item.checked}
                        role="checkbox"
                        aria-checked={item.checked ? 'true' : 'false'}
                        aria-label={`checkbox for ${item.Name}`}
                        />
                    </ListItemIcon>
                    <ListItemText primary={item.Name} secondary={item.Sublocation} />
                  </ListItemButton>
                  <ListItemSecondaryAction>
                    {item_in_cart ?
                      <>
                        <TextField
                          label="Qty"
                          sx={{minWidth: "60px", maxWidth: "60px", mr: 0.5}}
                          size="small"
                          value={item_in_cart.qty}
                          onChange={e => handleChangeQty(item, e)}
                          onBlur={() => handleBlur(item)}
                          slotProps={{htmlInput: {inputmode: "numeric", pattern: "[0-9]*"}}}
                        />
                        <FormControl
                          sx={{minWidth: "80px", maxWidth: "80px"}}
                          size="small"
                        >
                          <InputLabel id={`urgency${i}-label`}>Urgency</InputLabel>
                          <Select labelId={`urgency${i}-label`} id={`urgency${i}`} label="Urgency" value={item_in_cart.urgency} onChange={e => handleChangeUrgency(item, e)}>
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Med</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                          </Select>
                        </FormControl>
                      </>
                      :
                      <IconButton
                        edge="end"
                        onClick={() => handleAddToCart(item)}
                        aria-label={`add ${item.Name} to cart`}
                        >
                        <AddShoppingCartIcon fontSize='small' />
                      </IconButton>}
                  </ListItemSecondaryAction>
                </ListItem>
              )
            })}
          </React.Fragment>
        )}
      </List>
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          sx={{ mt: 1, mr: 1 }}
          onClick={handleSubmit}
          disabled={(list === undefined) || data.filter(x => x?.qty > 0).length === 0}
        >
          Next
        </Button>
        <Button
          onClick={handleBack}
          sx={{ mt: 1, mr: 1 }}
        >
          Back
        </Button>
      </Box>
    </Box>
  )
}
